import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseLoadingService } from '@fuse/services/loading';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { GroupJourneyStore, UserService, UserStore } from 'journey-store';

@Component({
  selector: 'settings-account',
  standalone: true,
  templateUrl: './account.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TextFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
  ],
})
export class SettingsAccountComponent implements OnInit {
  static NAV_URL = '/user/settings';

  private fuseLoading = inject(FuseLoadingService);

  private userService = inject(UserService);
  private userStore = inject(UserStore);
  private gjStore = inject(GroupJourneyStore);
  private confirmService = inject(FuseConfirmationService);
  private transloco = inject(TranslocoService);
  
  user = this.userStore.profile();

  profilePictureUrl: string = undefined;

  accountForm: UntypedFormGroup;
  confirmForm: UntypedFormGroup;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }
  /**
   * Constructor
   */
  constructor(private _formBuilder: UntypedFormBuilder, private dialog: MatDialog) {
    effect(() => {
      const user = this.userStore.profile();
      if (user) {
        this.accountForm.patchValue({
          name: user.name ?? '',
          email: user.email ?? '',
          about: user.about ?? '',
          phone: user.phone ?? '',
        });

        if (user.avatar && user.avatar.length > 0) {
          this.profilePictureUrl = user.avatar;//`${user.avatar}?cb=${Date.now()}`;
        } else {
          this.profilePictureUrl = null;
        }

        this.accountForm.enable();
        this.accountForm.get('email')?.disable();

      }
    });

    this.confirmForm = this._formBuilder.group({
      title: this.transloco.translate('pages.profile-account.remove-avatar'),
      message: this.transloco.translate('pages.profile-account.confirm-remove-avatar'),
      icon: this._formBuilder.group({
        show: true,
        name: 'heroicons_outline:exclamation-triangle',
        color: 'warning',
      }),
      actions: this._formBuilder.group({
        confirm: this._formBuilder.group({
          show: true,
          label: this.transloco.translate('pages.profile-account.remove'),
          color: 'warn',
        }),
        cancel: this._formBuilder.group({
          show: true,
          label: this.transloco.translate('pages.profile-account.cancel'),
        }),
      }),
      dismissible: true,
    });

  }

  ngOnInit(): void {
    // Create the form
    this.accountForm = this._formBuilder.group({
      name: [''],
      email: [''],
      about: [''],
      phone: [''],
    });
    this.accountForm.disable();
  }

  async onProfilePicSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      this.fuseLoading.show();

      // Optional preview
      const reader = new FileReader();
      reader.onload = () => {
        this.profilePictureUrl = reader.result as string;
      };
      reader.readAsDataURL(file);

      try {
        const pngBlob = file.type === 'image/png' ? file : await this.userService.convertImageToPng(file);
        const url = await this.userService.updateAvatar(new File([pngBlob], 'avatar.png', { type: 'image/png' }));

        console.log('Uploaded avatar URL:', url);
        this.profilePictureUrl = url;

        const result = await this.userStore.updateUser({ avatarUrl: url });
        const refreshedAvatarUrl = `${url}?cb=${Date.now()}`
        await this.gjStore.forceUserAvatarRefresh(refreshedAvatarUrl);
      } catch (err) {
        console.error('Failed to upload avatar', err);
      }
      //Save the avatar URL to the user profile
      this.fuseLoading.hide();

    }
  }

  async removeAvatar() {
    const dialogRef = this.confirmService.open(
      this.confirmForm.value
    );

    // Subscribe to afterClosed from the dialog reference
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.fuseLoading.show();
        this.userStore.removeAvatar().then(() => {
          this.gjStore.forceUserAvatarRefresh('').then(() => {
            this.fuseLoading.hide();
          });
        });
      }
    });
  }

  async saveProfile() {
    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      return;
    }

    const formData = this.accountForm.value;
    console.log('Form data to save:', formData);

    // Optionally call service to persist it
    this.fuseLoading.show();
    this.accountForm.disable();
    const result = await this.userStore.updateUser(
      {
        name: formData.name,
        about: formData.about,
        phone: formData.phone,
      });

    // this.accountForm.enable();
    // this.accountForm.get('email')?.disable();

    this.fuseLoading.hide();

  }
}
